import { useEffect, useMemo, useRef, useState } from 'react';
import Header from '@components/shared/Header/Header';
import { Alert } from '@components/shared/Alert/Alert';
import EnterLicensePlate from '@screens/EnterLicensePlate/EnterLicensePlate';
import OfferSelection from '@screens/OfferSelection/OfferSelection';
import YourInfo from '@screens/YourInfo/YourInfo';
import OffRamp from '@screens/OffRamp/OffRamp';
import EnterLicensePlateManually from '@screens/EnterLicensePlateManually/EnterLicensePlateManually';
import YourInformationManually from '@screens/YourInformationManually/YourInformationManually';
import { useFormContext } from '@context/FormContext';
import { Screens } from '@shared/constants/screens';

import NextSteps from '@screens/NextSteps/NextSteps';
import ChecklistStatus from '@screens/ChecklistStatus/ChecklistStatus';
import Disqualified from '@screens/Disqualified/Disqualified';
import Error from '@screens/Error/Error';

import FinalOffer from '@screens/FinalOffer/FinalOffer';
import VehicleInfoConfirm from '@screens/VehicleInfoConfirm/VehicleInfoConfirm';
import VerifyMobileNumber from '@screens/VerifyMobileNumber/VerifyMobileNumber';
import SoftPullAcknowledgement from '@screens/SoftPullAcknowledgement/SoftPullAcknowledgement';
import VerifyMobileNumberCheckYourPhone from '@screens/VerifyMobileNumber/VerifyMobileNumberCheckYourPhone';
import VerifyMobileNumberComplete from '@screens/VerifyMobileNumber/VerifyMobileNumberComplete';
import VehicleManualEnter from '@screens/VehicleManualEnter/VehicleManualEnter';
import RecaptchaLogo from '@components/RecaptchaLogo/RecaptchaLogo';
import FailRecaptcha from '@components/FailRecaptcha/FailRecaptcha';
import IdentityFailed from '@screens/IdentityFailed/IdentityFailed';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import DisqualifiedLowCreditScore from '@screens/Disqualified/DisqualifiedLowCreditScore';
import DisqualifiedDefaultCreditScore from '@screens/Disqualified/DisqualifiedDefaultCreditScore';
import LoanSelectionPage from '@screens/LenderInformation/LoanSelectionPage';
import { fetchProgressHistory } from '@services/progressHistory';
import { CDX_ACTIONS, ErrorResponse, ILicensePlateInfoForm } from 'types/form';
import TwoYearsAddressHistory from '@screens/TwoYearsAddressHistory/TwoYearsAddressHistory';
import CurrentAddressInfo from '@screens/CurrentAddressInfo/CurrentAddressInfo';
import CurrentAddressInfoManually from '@screens/CurrentAddressInfo/CurrentAddressInfoManually';
import LenderInformation from '@screens/LenderInformation/LenderInformation';
import EmploymentAndIncome from '@screens/EmploymentAndIncome/EmploymentAndIncome';
import OverlaySpinner from '@components/shared/OverlaySpinner/OverlaySpinner';
import { getContactDetailsFromSourceContactId } from '@services/contactDetails';
import { URL_METADATA_KEY } from '@shared/constants/source';
import { useAppContext } from '@context/appContext';
import { defaultScreenOrder } from '@shared/constants/defaultScreenOrder';
import { APP_ACTIONS } from '@context/appReducer';
import { setLocalStorage } from 'utils/setLocalStorage';
import { buildFlowTransitions } from 'utils/buildFlowTransitions';
import { getScreenOrder } from '@services/screenOrder';
import { Address } from 'types/proveIdentity';

const mapMileageToDropdownValue = (mileage: number): string => {
  if (mileage > 150000) return 'Over 150,000 miles';
  const lowerBound = Math.floor(mileage / 10000) * 10000;
  const upperBound = lowerBound + 10000;
  return lowerBound === 0 ? '0-10,000' : `${lowerBound.toLocaleString()}-${upperBound.toLocaleString()}`;
};

const StepFormContainer = ({ initValues = {} }) => {
  const { currentStep, apiError, isLoading, dispatch, handleStep, updateInitValues, updateVerifyYourInformationForm, widgetInitVariables } = useFormContext();
  const { appDispatch } = useAppContext();
  const { trackSystemError } = useAnalytics();
  const [isProgressHistoryLoading, setIsProgressHistoryLoading] = useState(false);
  const [isContactDetailsLoading, setIsContactDetailsLoading] = useState(true);
  const [hasMetadata, setHasMetadata] = useState(false);
  const hasCalledProgressHistoryRef = useRef(false);
  const scrollToTargetRef = useRef<HTMLDivElement | null>(null);

  const containerClass =
    process.env.REACT_APP_IS_WIDGET === 'true' ? 'iLendingCDX__widget-container' : 'iLendingCDX__spa-container';

  const getMetadataFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get(URL_METADATA_KEY);
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const initMetadataFlow = async () => {
      const metadata = getMetadataFromUrl();
      if (!metadata) {
        if (!signal.aborted) {
          setIsContactDetailsLoading(false);
          const screenOrder = defaultScreenOrder;
          const flowTransitions = buildFlowTransitions(screenOrder);

          appDispatch({
            type: APP_ACTIONS.SET_SCREEN_ORDER,
            payload: {
              screenOrder,
              flowTransitions,
            },
          });
        }
        return;
      }
      setHasMetadata(true);
  
      try {
        if (!signal.aborted) {
          setIsContactDetailsLoading(true);
        }
        const screenOrderResponse = await getScreenOrder(metadata, signal);
        if (signal.aborted) return;
        if (screenOrderResponse) {
          if ('error' in screenOrderResponse && screenOrderResponse.error != null) {
            dispatch({
              type: CDX_ACTIONS.GET_API_ERROR,
              payload: screenOrderResponse as ErrorResponse,
            });
          } else if ('cdxPageFlows' in screenOrderResponse) {
            const screenOrder = screenOrderResponse.cdxPageFlows ?? defaultScreenOrder;
            const flowTransitions = buildFlowTransitions(screenOrder);

            appDispatch({
              type: APP_ACTIONS.SET_SCREEN_ORDER,
              payload: {
                screenOrder,
                flowTransitions,
              },
            });
            const metaDataContactDetails ={
              contactId: screenOrderResponse.contactId,
              leadId: screenOrderResponse.leadId,
              assetId: screenOrderResponse.assetId
            };
            const contactDetailsResponse = await getContactDetailsFromSourceContactId(metaDataContactDetails, signal);
            if (signal.aborted) return;
            if (contactDetailsResponse) {
              if ('error' in contactDetailsResponse && contactDetailsResponse.error) {
                dispatch({
                  type: CDX_ACTIONS.GET_API_ERROR,
                  payload: contactDetailsResponse as ErrorResponse,
                });
              } else {
                dispatch({
                  type: CDX_ACTIONS.GET_CONTACT_DETAILS_SUCCESS,
                  payload: contactDetailsResponse,
                });
                const { assetId, asset, contact } = contactDetailsResponse;
                const { vin, year, make, model, mileage } = asset ?? {};
                const { cellPhone, dateOfBirth } = contact ?? {};

                const contactDetails = {
                  contactId: contactDetailsResponse?.contactId,
                  leadId: contactDetailsResponse?.leadId,
                  assetId: contactDetailsResponse?.assetId || null,
                  timeStamp: new Date().toISOString(),
                  addresses: contact?.addresses ?? [],
                  leadSourceId: screenOrderResponse?.leadSourceId || null
                };
            
                setLocalStorage(contactDetails, 'contactData');
                if (vin) {
                  const estimatedMileageRange = mapMileageToDropdownValue(Number(mileage) || 0);
      
                  const licensePlateFormData: Partial<ILicensePlateInfoForm> = {
                    plateNumber: '',
                    plateState: '',
                    estimatedMileageRange,
                  };
                  if (estimatedMileageRange) {
                    dispatch({
                      type: CDX_ACTIONS.SET_LICENSE_PLATE_INFO_FORM,
                      payload: licensePlateFormData,
                    });
                  }
                  if (year && make && model) {
                    dispatch({
                      type: 'SUBMIT_LICENSE_PLATE_INFO_SUCCESS',
                      payload: {
                        vin,
                        year: String(year),
                        make,
                        model,
                        isSuccess: true,
                        assetId,
                        message: null
                      },
                    });
                  }
                }
                  if (cellPhone && dateOfBirth && screenOrderResponse?.leadSourceId) {
                    dispatch({
                      type: 'SET_VERIFY_YOUR_IDENTITY_FORM',
                      payload: {
                        dateOfBirth: dateOfBirth.split('T')[0],
                        phone: cellPhone,
                      },
                    });
                  }
               
                  if (contact && screenOrderResponse?.leadSourceId) {
                    const { firstName, lastName, addresses, cellPhone, ssn, dateOfBirth, email , residencyStatusType } = contact;

                    const formattedAddresses: Address[] = addresses?.map(addr => ({
                      address: addr.streetLine1 ?? '',
                      extendedAddress: addr.streetLine2 ?? '',
                      postalCode: addr.zip ?? '',
                      region: addr.state ?? '',
                      city: addr.city ?? '',
                      type: addr.type ?? '',
                      isCurrent: addr.isCurrent ?? false,
                      monthsAtAddress: addr.monthsAtAddress ?? 0,
                      monthlyPayment: addr.monthlyPayment ?? 0,
                    })) || [];
                  
                    const contactInfo = {
                      firstName: firstName ?? '',
                      lastName: lastName ?? '',
                      addresses: formattedAddresses ?? [],
                      phone: cellPhone ?? '',
                      ssn: ssn ?? '',
                      dateOfBirth: dateOfBirth ?? '',
                      email: email ?? '',
                      residency: residencyStatusType ?? '',
                    };
                  
                    updateVerifyYourInformationForm(contactInfo);
                  }
                
              }
            }
          }
        }
      } catch (err) {
        console.error('Get contact details from source contact id failed', err);
      } finally {
        if (!signal.aborted) {
          setIsContactDetailsLoading(false);
        }
      }
    };
    initMetadataFlow();
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    updateInitValues(initValues);
  }, [initValues]);

  useEffect(() => {
    if (currentStep && scrollToTargetRef.current) {
      scrollToTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const isOlderThan30Days = (timestamp: string) => {
    if (!timestamp) return false;

    const thirtyDaysInMilliseconds = 30 * 24 * 60 * 60 * 1000;
    const timestampInMilliseconds = new Date(timestamp).getTime();
    const currentTime = Date.now();

    return currentTime - timestampInMilliseconds > thirtyDaysInMilliseconds;
  };

  useEffect(() => {
    if (hasCalledProgressHistoryRef.current) return;
    hasCalledProgressHistoryRef.current = true;
    const checkProgress = async () => {
      const storedData = localStorage.getItem('iLendingCDX');
      if (!storedData) {
        if (!currentStep) {
          handleStep(Screens.ENTER_LICENSE_PLATE);
        }
        return;
      }

      const storedDetails = JSON.parse(storedData);
      const contactId = storedDetails?.contactData?.contactId;
      const leadId = storedDetails?.contactData?.leadId;
      const timestamp = storedDetails?.contactData?.timeStamp;

      if (!contactId) {
        localStorage.clear();
        handleStep(Screens.ENTER_LICENSE_PLATE);
        return;
      }

      if (isOlderThan30Days(timestamp)) {
        localStorage.clear();
        window.location.reload();
        handleStep(Screens.ENTER_LICENSE_PLATE);
        return;
      }

      if (contactId && leadId ) {
        setIsProgressHistoryLoading(true);
        const progressHistoryDetails = await fetchProgressHistory(contactId, leadId);

        if (progressHistoryDetails) {
          if ('error' in progressHistoryDetails) {
            if (progressHistoryDetails?.error?.status === 404) {
              handleStep(Screens.ENTER_LICENSE_PLATE);
              localStorage.clear();
            } else {
              dispatch({
                type: CDX_ACTIONS.GET_API_ERROR,
                payload: progressHistoryDetails,
              });
            }
            setIsProgressHistoryLoading(false);
          } else {
            setIsProgressHistoryLoading(false);
            if (progressHistoryDetails?.nextScreen) {
              if (progressHistoryDetails?.nextScreen === Screens.OFF_RAMP) {
                handleStep(Screens.ENTER_LICENSE_PLATE);
              } else {
                handleStep(progressHistoryDetails?.nextScreen);
              }
            } else {
              handleStep(Screens.ENTER_LICENSE_PLATE);
            }
          }
        }
      }
    };

    checkProgress();
  }, []);

  useEffect(() => {
    if (apiError?.error?.message) {
      trackSystemError('ERROR', { errorDescription: apiError?.error?.message ?? '' });
    }
  }, [apiError?.error?.message]);

  const alertMessage = useMemo(() => apiError?.error?.message, [apiError?.error?.message]);

  const isRecaptchaError = useMemo(
    () => apiError?.error?.status === 401 && (typeof alertMessage === 'string' ? alertMessage : '').includes('bot'),
    [alertMessage]
  );

  // const isLoading = useShowLoadingAnimation(appState.ui.isLoading);
  // const loadingImage = appState.ui.loadingImage;

  if (apiError && apiError?.error?.status === 500 && !isLoading) {
    return <Error />;
  }
  if (!isLoading && !!alertMessage && isRecaptchaError) {
    return (
      <div className={containerClass}>
        <Header />
        <div className="iLendingCDX-widget-form">
          <FailRecaptcha />
        </div>
      </div>
    );
  }

  if (hasMetadata && (isContactDetailsLoading || isProgressHistoryLoading)) {
    return (
      <div className={containerClass}>
        <Header />
        <OverlaySpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <div
        ref={scrollToTargetRef}
        className={containerClass}
        style={{ borderRadius: widgetInitVariables?.branding?.roundCorners ? '8px' : '0px' }}
      >
        {!widgetInitVariables?.branding?.hideTopNav && <Header />}
        {apiError?.error?.status !== 500 && !isLoading && alertMessage && <Alert message={alertMessage} />}
        <div className="iLendingCDX-widget-form">
          {currentStep === Screens.ENTER_LICENSE_PLATE && <EnterLicensePlate />}
          {currentStep === Screens.ENTER_LICENSE_PLATE_MANUALLY && <EnterLicensePlateManually />}
          {currentStep === Screens.VEHICLE_MANUAL_ENTER && <VehicleManualEnter />}
          {currentStep === Screens.VERIFY_VEHICLE_INFO && <VehicleInfoConfirm />}
          {currentStep === Screens.VERIFY_MOBILE_NUMBER && <VerifyMobileNumber />}
          {currentStep === Screens.VERIFY_MOBILE_NUMBER_CHECK_YOUR_PHONE && <VerifyMobileNumberCheckYourPhone />}
          {currentStep === Screens.VERIFY_MOBILE_NUMBER_COMPLETE && <VerifyMobileNumberComplete />}
          {/* {currentStep === Screens.VERIFY_YOUR_INFO && <VerifyYourInfo />} */}
          {currentStep === Screens.YOUR_INFO && <YourInfo />}
          {currentStep === Screens.YOUR_INFORMATION_MANUALLY && <YourInformationManually />}
          {currentStep === Screens.SOFT_PULL_ACKNOWLEDGEMENT && <SoftPullAcknowledgement />}
          {currentStep === Screens.OFFER_SELECTION && <OfferSelection />}
          {currentStep === Screens.OFF_RAMP && <OffRamp />}
          {currentStep === Screens.DISQUALIFIED && <Disqualified />}
          {currentStep === Screens.DISQUALIFIED_DECLINE_LOW_CREDIT_SCORE && <DisqualifiedLowCreditScore />}
          {currentStep === Screens.DISQUALIFIED_DECLINE_DEFAULT_CREDIT_SCORE && <DisqualifiedDefaultCreditScore />}
          {currentStep === Screens.NEXT_STEPS && <NextSteps />}
          {currentStep === Screens.CHECKLIST_STATUS && <ChecklistStatus />}
          {currentStep === Screens.FINAL_OFFER && <FinalOffer />}
          {currentStep === Screens.IDENTITY_FAILED && <IdentityFailed />}
          {currentStep === Screens.LOAN_SELECTION && <LoanSelectionPage />}
          {currentStep === Screens.LENDER_INFO && <LenderInformation />}

          {/* {currentStep === Screens.TWO_YEARS_ADDRESS_HISTORY && <TwoYearsAddressHistory />} */}
          {/* {currentStep === Screens.CURRENT_ADDRESS_INFO && <CurrentAddressInfo />} */}
          {currentStep === Screens.TWO_YEARS_ADDRESS_HISTORY && <TwoYearsAddressHistory />}
          {currentStep === Screens.CURRENT_ADDRESS_INFO && <CurrentAddressInfo />}
          {currentStep === Screens.CURRENT_ADDRESS_INFO_MANUALLY && <CurrentAddressInfoManually />}

          {currentStep === Screens.EMPLOYMENT_AND_INCOME && <EmploymentAndIncome />}
        </div>
        {(isProgressHistoryLoading || isContactDetailsLoading) && <OverlaySpinner size="lg" />}
      </div>
      <RecaptchaLogo />
    </>
  );
};

export default StepFormContainer;
